import * as React from 'react';
import { logger } from '../../../../commons/utils/logger';

export interface FederatedLoadingProps {
  componentName: string;
  error: Error | undefined;
  hasTimedOut: boolean;
  pastDelay: boolean;
}

export interface FederatedAfterLoadProps {
  module: any;
  loadTimeInMs: number;
}

export type FederatedLoader = () => Promise<any>;
export type WrappedFederatedLoader = (
  importFunction: FederatedLoader,
) => FederatedLoader;
type FederatedAfterLoadFn = (afterLoadProps: FederatedAfterLoadProps) => void;
type FederatedRenderFn = (module: any) => React.ReactElement | null;

export interface FederatedRemoteProps {
  loader: FederatedLoader;
  timeout: number;
  afterLoad: FederatedAfterLoadFn;
  render: FederatedRenderFn;
}

/**
 * This react component lazily loads a remote module and renders it using the provided render method.
 *
 * @param loader {Loader} Function to be called to load the remote module.
 * @param timeout {number} This property is used to set a time limit of timeout ms for the remote component to be
 *        loaded.
 * @param afterLoad {AfterLoadFn} This function is called after the remote module has been loaded.
 * @param render {RenderFn} This function is called to render a react component after the remote module has been loaded.
 * @constructor Creates Remote react component.
 */
const FederatedRemote: React.VFC<FederatedRemoteProps> = ({
  loader,
  timeout,
  afterLoad,
  render,
}) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [hasTimedOut, setHasTimedOut] = React.useState<boolean>(false);
  const [module, setModule] = React.useState<any>(undefined);

  React.useEffect(() => {
    // Set hasTimedOut to true if loading takes longer than timeout prop
    let timeoutTimer: NodeJS.Timeout;
    if (timeout) {
      timeoutTimer = setTimeout(() => setHasTimedOut(true), timeout);
    }

    const startTime = new Date().getTime();

    const loaderTimeoutID = setTimeout(() => {
      loader()
        .then((mod: any) => {
          setModule(mod);

          // Run afterLoad method
          if (afterLoad)
            afterLoad({
              module: mod,
              loadTimeInMs: new Date().getTime() - startTime,
            });
        })
        .catch((e) => {
          logger.error('Failed to load remote component: ', e);
        })
        .finally(() => {
          // Clear timeout timers
          if (timeoutTimer) clearTimeout(timeoutTimer);

          setIsLoading(false);
        });
    });

    return () => {
      clearTimeout(loaderTimeoutID);
    };
  }, []);

  // show nothing when loading or timed out
  const showNothing = isLoading || hasTimedOut;
  return showNothing ? <></> : render(module);
};

export default FederatedRemote;
